import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ContactFormSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    companySize: '',
    message: '',
    preferredContact: 'phone',
    urgency: 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const translations = {
    en: {
      title: "Get In Touch",
      subtitle: "Fill out the form below and our ERP specialists will contact you within 2 hours",
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        company: "Company Name",
        companySize: "Company Size",
        message: "How can we help you?",
        preferredContact: "Preferred Contact Method",
        urgency: "Urgency Level"
      },
      options: {
        companySize: {
          small: "Small (1-50 employees)",
          medium: "Medium (51-200 employees)",
          large: "Large (200+ employees)"
        },
        preferredContact: {
          phone: "Phone Call",
          email: "Email",
          whatsapp: "WhatsApp",
          video: "Video Call"
        },
        urgency: {
          low: "Low - General Inquiry",
          medium: "Medium - Planning Implementation",
          high: "High - Urgent Requirement"
        }
      },
      submitButton: "Send Message",
      submitting: "Sending...",
      success: "Message sent successfully! We\'ll contact you soon.",
      error: "Failed to send message. Please try again."
    },
    hi: {
      title: "संपर्क में रहें",
      subtitle: "नीचे दिया गया फॉर्म भरें और हमारे ERP विशेषज्ञ 2 घंटे के भीतर आपसे संपर्क करेंगे",
      form: {
        name: "पूरा नाम",
        email: "ईमेल पता",
        phone: "फोन नंबर",
        company: "कंपनी का नाम",
        companySize: "कंपनी का आकार",
        message: "हम आपकी कैसे मदद कर सकते हैं?",
        preferredContact: "पसंदीदा संपर्क विधि",
        urgency: "तात्कालिकता स्तर"
      },
      options: {
        companySize: {
          small: "छोटी (1-50 कर्मचारी)",
          medium: "मध्यम (51-200 कर्मचारी)",
          large: "बड़ी (200+ कर्मचारी)"
        },
        preferredContact: {
          phone: "फोन कॉल",
          email: "ईमेल",
          whatsapp: "व्हाट्सऐप",
          video: "वीडियो कॉल"
        },
        urgency: {
          low: "कम - सामान्य पूछताछ",
          medium: "मध्यम - कार्यान्वयन की योजना",
          high: "उच्च - तत्काल आवश्यकता"
        }
      },
      submitButton: "संदेश भेजें",
      submitting: "भेजा जा रहा है...",
      success: "संदेश सफलतापूर्वक भेजा गया! हम जल्द ही आपसे संपर्क करेंगे।",
      error: "संदेश भेजने में विफल। कृपया पुनः प्रयास करें।"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Here you would typically send the data to your backend
      console.log('Form data submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        companySize: '',
        message: '',
        preferredContact: 'phone',
        urgency: 'medium'
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.company && formData.message;

  return (
    <section id="contact-form" className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.name} *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.form.name}
                  required
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.email} *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.form.email}
                  required
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.phone} *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  required
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.company} *
                </label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder={t.form.company}
                  required
                  className="w-full"
                />
              </div>
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                {t.form.companySize}
              </label>
              <select
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select company size</option>
                <option value="small">{t.options.companySize.small}</option>
                <option value="medium">{t.options.companySize.medium}</option>
                <option value="large">{t.options.companySize.large}</option>
              </select>
            </div>

            {/* Preferred Contact Method */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.preferredContact}
                </label>
                <select
                  name="preferredContact"
                  value={formData.preferredContact}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="phone">{t.options.preferredContact.phone}</option>
                  <option value="email">{t.options.preferredContact.email}</option>
                  <option value="whatsapp">{t.options.preferredContact.whatsapp}</option>
                  <option value="video">{t.options.preferredContact.video}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  {t.form.urgency}
                </label>
                <select
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="low">{t.options.urgency.low}</option>
                  <option value="medium">{t.options.urgency.medium}</option>
                  <option value="high">{t.options.urgency.high}</option>
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                {t.form.message} *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t.form.message}
                required
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                variant="primary"
                disabled={!isFormValid || isSubmitting}
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta px-8 py-4"
                iconName={isSubmitting ? "Loader" : "Send"}
                iconPosition="left"
                iconSize={20}
              >
                {isSubmitting ? t.submitting : t.submitButton}
              </Button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <span className="text-success font-medium">{t.success}</span>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="XCircle" size={20} color="var(--color-error)" />
                  <span className="text-error font-medium">{t.error}</span>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;